import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';

import theme from '@styles/theme';
import { css } from '@emotion/react';
import {
  SaleIcon,
  TimeIcon,
  AutoRenewIcon,
  HourGlassIcon,
  CoinColorIcon,
} from 'public/assets';
import { PURCHASE_STORY } from '@api/story';
import FullScreenPopupContainer from './container/FullScreenPopupContainer';
import { useGetUserQuery } from '@/operations/queries/user/__generated__/getUser.generated';

import {
  payTicketAll,
  viewPurchaseStoryPopup,
  touchPurchaseStoryPopup,
  viewAllChapterPurchasePopup,
  payTicketForStory,
} from '@lib/eventManager';
import { Story_Play_Type } from '@/baseType';
import { useAppDispatch } from '@/store/hooks';
import { alertMessage, setToastMessage } from '@/slices/alertSlice';
import { SERVER_ERROR_CODE, STORAGE_KEY } from '@/common/values';

import SPImage from '../image/SPImage';
import NoticeWholePurchaseInfoPopup from './NoticeWholePurchaseInfoPopup';

interface IWholePurchaseInfoPopup {
  storyName?: string;
  storyId?: number;
  chapterIndex?: number;
  originalCoin?: number;
  lastPreviewChapterIndex?: number;
  storyCoin: number;
  isShow: boolean;
  onClose: () => void;
  nextChapterId?: number;
  storyBundleId?: number;
  storyLanguage?: string;
  storyGenre?: string;
  playType?: Story_Play_Type;
  coverImage?: string;
  discountRate: number;
  hasAdultChapter: boolean;
}

interface IBenefitItem {
  icon: 'sale' | 'time' | 'autoRenew' | 'hourGlass';
  topText: string;
  bottomText: string;
  delay: number;
}

const BenefitItem = ({ icon, topText, bottomText, delay }: IBenefitItem) => {
  return (
    <div css={benefitItemWrapper({ delay })}>
      {icon === 'sale' ? (
        <SaleIcon className="icon" width={31} height={31} />
      ) : icon === 'time' ? (
        <TimeIcon className="icon" width={31} height={31} />
      ) : icon === 'autoRenew' ? (
        <AutoRenewIcon className="icon" width={31} height={31} />
      ) : (
        <HourGlassIcon className="icon" width={31} height={31} />
      )}
      <div className="benefit-text">
        <p className="top-text">{topText}</p>
        <p className="bottom-text">{bottomText}</p>
      </div>
    </div>
  );
};

const WholePurchaseInfoPopup = ({
  storyName,
  storyId,
  storyCoin,
  chapterIndex,
  originalCoin,
  lastPreviewChapterIndex,
  isShow,
  onClose,
  nextChapterId,
  storyBundleId,
  storyLanguage,
  storyGenre,
  playType,
  coverImage,
  discountRate,
  hasAdultChapter,
}: IWholePurchaseInfoPopup) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [
    showNoticeWholePurchaseInfoPopup,
    setShowNoticeWholePurchaseInfoPopup,
  ] = useState<boolean>(false);
  const { data: userData } = useGetUserQuery();

  const [purchaseStoryMutate] = useMutation<{
    purchaseStory: { ok: number };
  }>(PURCHASE_STORY, {
    onCompleted: (data) => {
      if (data.purchaseStory.ok === 1) {
        router.reload();
        onClose();
        payTicketAll({
          storyId: storyId,
          ticketCount: storyCoin,
          storyNameAndChapterIndex: `${storyName}_${chapterIndex}`,
        });
        payTicketForStory({
          amount: storyCoin,
          storyId: storyId || 0,
          storyBundleId,
          storyName: storyName || '',
          storyLanguage,
          storyGenre,
          playType:
            playType === Story_Play_Type.Interactive ? 'interactive' : 'novel',
        });
        // nextChapter 아이디 정보가 있으면 다음화로 이동, 없으면 작품상세화면으로 이동.
        // TODO: 로직 개선 필요
        if (nextChapterId) {
          router.replace({
            pathname: `/chatstory/${nextChapterId}`,
            query: {
              prevScreen: 'chat',
            },
          });
        } else {
          router.replace(`/story/${storyId}`);
        }
      } else {
        onClose();
        dispatch(
          alertMessage({
            message: t('common_popup_description_error_occurred'),
            type: 'warning',
          }),
        );
      }
    },
    onError: (error) => {
      onClose();
      if (
        error.message.includes(SERVER_ERROR_CODE.ADULT_VERIFICATION_REQUIRED)
      ) {
        dispatch(
          setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
        );

        const redirectInfo = JSON.stringify({
          redirectUrl: `/story/${storyId}`,
          createdAt: new Date(),
        });
        localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

        router.replace({
          pathname: `/adult`,
          query: {
            url: `/story/${storyId}`,
            storyId,
          },
        });
        return;
      }
      dispatch(setToastMessage(t('common_popup_description_error_occurred')));
    },
  });

  useEffect(() => {
    if (!isShow) return;

    viewPurchaseStoryPopup({
      storyName,
      storyId,
      fullPrice: originalCoin,
      price: storyCoin,
      userCoin: userData?.getUser?.currentCoin,
    });

    viewAllChapterPurchasePopup({
      storyName,
      storyId: String(storyId),
      fullCoin: originalCoin,
      coin: storyCoin,
      lastPreviewChapterIndex,
      purchasedChapter: storyCoin,
      contentType: 'interactive',
    });
  }, [isShow]);

  const onPurchase = async () => {
    touchPurchaseStoryPopup({
      storyName,
      storyId,
      fullPrice: originalCoin,
      price: storyCoin,
      userCoin: userData?.getUser?.currentCoin,
    });

    if (userData?.getUser?.isAnonymous) {
      setShowNoticeWholePurchaseInfoPopup(true);
      return;
    }

    if (
      hasAdultChapter &&
      !userData?.getUser?.certificatedAt &&
      userData?.getUser?.isExpiredCertification
    ) {
      dispatch(
        setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
      );
      const redirectInfo = JSON.stringify({
        redirectUrl: `/story/${storyId}`,
        createdAt: new Date(),
      });
      localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

      await router.push({
        pathname: '/adult',
        query: {
          url: `/story/${storyId}`,
          storyId,
        },
      });
      onClose();
      return;
    }

    if ((userData?.getUser?.currentCoin || 0) < storyCoin) {
      const redirectInfo = JSON.stringify({
        redirectUrl: `/story/${storyId}`,
        createdAt: new Date(),
      });
      localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

      await router.push({
        pathname: '/store',
        query: {
          coin: storyCoin,
          url: `/story/${storyId}`,
        },
      });
      onClose();
      return;
    }

    purchaseStoryMutate({
      variables: {
        storyId: storyId,
        price: storyCoin,
      },
    });
  };

  if (!isShow) {
    return null;
  }

  return (
    <FullScreenPopupContainer onClose={onClose}>
      <div css={container}>
        <div className="cover">
          <div className="dark-layer top" />
          <div className="image-wrapper">
            {coverImage && (
              <SPImage src={coverImage} objectFit="cover" layout="fill" />
            )}
          </div>
          <div className="dark-layer bottom">
            <div className="title-wrapper">
              <h1 className="title">
                {t('store_screen_label_benefit_unlimited_pass')}
              </h1>
              <p className="description">
                {t('storydetail_popup_label_purchase_now_benefit', {
                  storyName,
                })}
              </p>
            </div>
          </div>
        </div>
        <div className="benefit-list">
          <BenefitItem
            icon="sale"
            topText={t('store_screen_description_benefit_one')}
            bottomText={t('store_screen_title_benefit_one', {
              value: discountRate,
            })}
            delay={0.2}
          />
          <BenefitItem
            icon="time"
            topText={t('store_screen_description_benefit_two')}
            bottomText={t('store_screen_title_benefit_two')}
            delay={0.4}
          />
          <BenefitItem
            icon="autoRenew"
            topText={t('store_screen_description_benefit_three')}
            bottomText={t('store_screen_title_benefit_three')}
            delay={0.6}
          />
          <BenefitItem
            icon="hourGlass"
            topText={t('store_screen_description_benefit_four')}
            bottomText={t('store_screen_title_benefit_four')}
            delay={0.8}
          />
        </div>
        {/* TODO: 공통 Button 컴포넌트 사용하기 */}
        <div className="button-wrapper">
          <button className="whole-purchase-button" onClick={onPurchase}>
            <p className="whole-purchase-button-text">
              {t('store_popup_button_buy_unlimited_story')}
              <CoinColorIcon />
              {storyCoin}
            </p>
          </button>
        </div>
      </div>
      <NoticeWholePurchaseInfoPopup
        isShow={showNoticeWholePurchaseInfoPopup}
        storyId={storyId}
        onClose={() => {
          onClose();
          setShowNoticeWholePurchaseInfoPopup(false);
        }}
      />
    </FullScreenPopupContainer>
  );
};

const container = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  color: ${theme.colors.white};
  font-family: 'Apple SD Gothic Neo';
  align-items: center;
  justify-content: center;
  font-size: 18px;
  height: 100%;

  .cover {
    width: 100%;

    .image-wrapper {
      position: absolute;
      top: 0;
      width: 100%;
      height: 375px;
    }

    .dark-layer {
      position: absolute;
      width: 800px;
      z-index: 1;

      @media screen and (max-width: ${theme.devices.mobile}) {
        width: 100%;
      }
    }

    .dark-layer.top {
      top: 0;
      height: 124px;
      background: linear-gradient(
        180deg,
        rgba(37, 37, 38, 0.6) 0%,
        rgba(37, 37, 38, 0) 100%
      );
    }

    .dark-layer.bottom {
      top: 158px;
      display: flex;
      justify-content: center;
      height: 217px;
      background: linear-gradient(180deg, rgba(36, 37, 38, 0) 0%, #1a1a1a 100%);

      .title-wrapper {
        position: absolute;
        bottom: -1.875em;
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 0.5em;

        .title {
          font-weight: 700;
          font-size: 1.5em;
          line-height: 28px;
        }

        .description {
          font-weight: 400;
          font-size: 0.8125em;
          line-height: 18px;
        }
      }
    }
  }

  .benefit-list {
    flex-direction: column;
    position: absolute;
    top: 405px;
    display: flex;
    justify-content: center;
    height: calc(100vh - 421px);
    min-height: 514px;
    padding: 0 1em 72px;
  }

  .button-wrapper {
    position: fixed;
    bottom: 0;
    z-index: 1;
    display: flex;
    margin: 1.25em 1em;

    .whole-purchase-button {
      flex-basis: 100%;
      width: calc(800px - 2.5em);
      height: 52px;
      border-radius: 0.375em;
      font: ${theme.font.body1Bold};
      cursor: pointer;
      background-color: ${theme.colors.primary500};
      color: ${theme.colors.white};
      border: 0.0625em solid ${theme.colors.primary500};

      .whole-purchase-button-text {
        display: flex;
        justify-content: center;
        gap: 0.25em;
      }

      @media screen and (max-width: ${theme.devices.mobile}) {
        width: calc(100vw - 2em);
      }
    }
  }

  @media screen and (max-width: ${theme.devices.mobile}) {
    font-size: 16px;
  }
`;

const benefitItemWrapper = ({ delay }: { delay: number }) => css`
  display: flex;
  gap: 1em;
  margin-bottom: 1.25em;
  animation: up ${delay}s ease-out;

  &:last-child {
    margin-bottom: 0;
  }

  .icon {
    flex-shrink: 0;
  }

  .benefit-text {
    display: flex;
    flex-direction: column;
    gap: 0.5em;

    .top-text {
      font-weight: 400;
      font-size: 0.8125em;
      line-height: 1.125em;
    }

    .bottom-text {
      font-weight: 700;
      font-size: 1em;
      line-height: 1.375em;
    }
  }

  @keyframes up {
    0% {
      transform: translateY(200px);
    }

    100% {
      transform: translateY(0);
    }
  }
`;

export default WholePurchaseInfoPopup;
